import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, type UseFormReturn, useForm } from "react-hook-form";
import { FormWrapper } from "@/components";
import {
  BestillingFormErrorSummary,
  type BestillingPageKey,
  useBestillingContext,
} from "@/features/bestillinger";
import {
  BestillingNavigationActions,
  FormNavigationBlocker,
  useBestillingNavigation,
} from "@/features/navigation";
import { NæringsbyggFields } from "@/features/shared/NaeringsbyggFields";
import { SelskapBoligTypeFields } from "@/features/shared/SelskapBoligTypeFields";
import { useSelskapsBoligType } from "@/features/shared/useSelskapsBoligType";
import { isDirty } from "@/utils";
import {
  bestillerSelskapFormSchema,
  type bestillerSelskapFormValues,
  getDefaultBestillerSelskapFormValues,
  translationPrefixSelskapForm,
} from "./BestillerSelskapFormSchemas";
import { Tekstfelt } from "./Tekstfelt";

const currentPageKey: BestillingPageKey = "/bestilling/ny/bestiller";

export function BestillerSelskapForm() {
  const { navigateNext } = useBestillingNavigation(currentPageKey);
  const { readOnly, bestilling, setBestillingData } = useBestillingContext();

  const form = useForm({
    resolver: zodResolver(bestillerSelskapFormSchema),
    values: getDefaultBestillerSelskapFormValues(bestilling.bestillerSelskap),
    disabled: readOnly,
    mode: "onSubmit",
  }) as UseFormReturn<bestillerSelskapFormValues>;

  const {
    isNæringsdrivende,
    isNæringsbyggMedBoliger,
    formDataIsNæringsbyggMedBoliger,
    formDataIsNæringsdrivende,
  } = useSelskapsBoligType(form);

  const updateBestillingQuery = (data: bestillerSelskapFormValues) => {
    // navigation blocker liker promise
    return Promise.resolve(
      setBestillingData({
        ...bestilling,
        bestillerPrivat: null, // Hvis bruker har valgt selskap, fylt ut, gått tilbake og valgt privat, så nullstilles bestillerPrivat
        bestillerSelskap: {
          kundenummer: data.kundenummer,
          målerId: data.målerId,
          antallBoenheter: formDataIsNæringsbyggMedBoliger(data)
            ? 1
            : Number(data.antallBoenheter),
          næringsdrivende: formDataIsNæringsdrivende(data),
          næringsarealProsent:
            formDataIsNæringsdrivende(data) && data.næringsarealProsent
              ? Number(data.næringsarealProsent)
              : undefined,
          boligType: data.boligType,
        },
      }),
    );
  };

  async function handleSubmit(data: bestillerSelskapFormValues) {
    if (isDirty(form.formState)) {
      await updateBestillingQuery(data);
    }
    await navigateNext();
  }

  return (
    <section>
      <FormProvider {...form}>
        <FormNavigationBlocker form={form} onSubmit={updateBestillingQuery} />
        <FormWrapper onSubmit={form.handleSubmit(handleSubmit)}>
          <SelskapBoligTypeFields form={form} />

          <Tekstfelt
            feltKey="kundenummer"
            maxLength={256}
            readOnly={readOnly}
            hjelpeTekst={true}
            labelTranslationPrefix={translationPrefixSelskapForm}
          />
          <Tekstfelt
            feltKey="målerId"
            maxLength={256}
            readOnly={readOnly}
            hjelpeTekst={true}
            labelTranslationPrefix={translationPrefixSelskapForm}
          />
          <NæringsbyggFields
            form={form}
            isNæringsbyggMedBoliger={isNæringsbyggMedBoliger}
            isNæringsdrivende={isNæringsdrivende}
            readOnly={readOnly}
          />

          <BestillingFormErrorSummary errors={form.formState.errors} />
          <BestillingNavigationActions
            className="pt-6"
            currentPageKey={currentPageKey}
            disableNext={readOnly}
          />
        </FormWrapper>
      </FormProvider>
    </section>
  );
}
